import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [recoveryToken, setRecoveryToken] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase appends token info to the URL hash after the user clicks the reset link:
    // /reset-password#access_token=xxx&type=recovery&...
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const token = params.get('access_token');
    const type = params.get('type');

    if (token && type === 'recovery') {
      setRecoveryToken(token);
    } else {
      setInvalid(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm) {
      return setError('Passwords do not match.');
    }
    if (form.password.length < 8) {
      return setError('Password must be at least 8 characters.');
    }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/update-password`,
        { password: form.password },
        { headers: { Authorization: `Bearer ${recoveryToken}` } }
      );
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-sand-50">
      <div className="hidden lg:flex flex-col justify-center px-16 bg-terra-400/10 border-r border-sand-200 w-2/5 shrink-0">
        <p className="font-heading text-4xl font-bold text-ink-900 mb-4 leading-tight">
          Set a new password
        </p>
        <p className="text-base text-ink-400 leading-relaxed">
          Choose a strong password you haven't used before.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-sm">
          {invalid && (
            <div className="text-center">
              <p className="font-heading text-xl font-semibold text-ink-900 mb-2">Link invalid or expired</p>
              <p className="text-sm text-ink-400 mb-6">
                This reset link has already been used or has expired. Request a new one.
              </p>
              <Link to="/forgot-password" className="text-sm text-terra-400 font-medium hover:text-terra-500 transition-colors">
                Request a new link
              </Link>
            </div>
          )}

          {done && (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-terra-400/10 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-terra-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <p className="font-heading text-xl font-semibold text-ink-900 mb-2">Password updated</p>
              <p className="text-sm text-ink-400">Redirecting you to sign in…</p>
            </div>
          )}

          {!invalid && !done && recoveryToken && (
            <>
              <p className="font-heading text-2xl font-semibold text-ink-900 mb-1">New password</p>
              <p className="text-sm text-ink-400 mb-8">Must be at least 8 characters.</p>

              {error && (
                <div className="mb-4 px-4 py-3 bg-uae-red/10 border border-uae-red/20 rounded-lg text-sm text-uae-red">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input
                  id="password"
                  label="New password"
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
                <Input
                  id="confirm"
                  label="Confirm password"
                  type="password"
                  value={form.confirm}
                  onChange={e => setForm({ ...form, confirm: e.target.value })}
                  placeholder="••••••••"
                  required
                />
                <Button type="submit" variant="primary" loading={loading} className="w-full justify-center mt-2">
                  Update password
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
